<template>
  <div :class="[isSmallScreen ? 'q-mt-lg' : 'q-mt-md', { 'q-mx-lg': !isColumnLayout && isSmallScreen }]">
    <div
      v-if="isEmptyPage"
      class="empty-block q-pa-xl flex column justify-center items-center text-center border-radius-20"
    >
      <img
        src="/empty-blog-cover-vector.svg"
        width="232"
        height="149"
        :alt="$t('label.emptyBlogPage')"
        class="q-mb-xl"
      >
      <div
        v-t="'text.noArticle'"
        class="fs-24 lh-32 q-mb-md"
      />
      <div
        class="empty-block-description lh-25"
        v-html="$t('text.noArticleDescription')"
      />
    </div>
    <RenderPostsList
      v-else
      :posts="postsForRender"
      :is-mobile="isSmallScreen"
      :is-column-layout="isColumnLayout"
    />

    <q-pagination
      v-if="totalPages > 1"
      :class="['q-mt-xl', { 'pagination-mobile': isSmallScreen }]"
      :model-value="renderStore.postsPage"
      :ripple="false"
      :max="totalPages"
      :max-pages="isSmallScreen ? 5 : 7"
      flat
      active-design="flat"
      @update:model-value="onNavigate"
    />
  </div>
</template>

<script setup>
import RenderPostsList from 'components/render/RenderPostsList.vue';
import { useRenderStore } from 'stores/render';
import { useMeta } from 'quasar';
import { computed } from 'vue';
import handleError from 'src/helpers/handleError';
import useSchemaOrgBlogData from 'src/composables/useSchemaOrgBlogData';
import useRender from 'src/composables/useRender';
import parseMetaTags from 'src/helpers/parseMetaTags';

const renderStore = useRenderStore();

const {
  isSmallScreen,
} = useRender();

const isEmptyPage = computed(() => renderStore.posts.length === 0);
const totalPages = computed(() => Math.ceil(renderStore.postsTotal / renderStore.perPage));
const postsForRender = computed(() => {
  if (renderStore.settings.metaFields.templateLayout === 'column' && !isSmallScreen.value) {
    return renderStore.posts.slice(1, renderStore.posts.length);
  }

  return renderStore.posts;
});
const isColumnLayout = computed(() => renderStore.settings?.metaFields?.templateLayout === 'column');

const schemaOrgData = useSchemaOrgBlogData();

const metaData = {
  title: renderStore.settings.metaFields.metaTitle || 'FunKITools',
  meta: {
    description: {
      name: 'description',
      content: renderStore.settings.metaFields.metaDescription,
    },
    keywords: {
      name: 'keywords',
      content: renderStore.settings.metaFields.metaKeywords,
    },
  },
  script: {
    ldJson: {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(schemaOrgData.value),
    },
  },
};

const customMetaTags = parseMetaTags(renderStore.settings.metaFields?.customMetaTag || '');

if (customMetaTags?.length) {
  customMetaTags.forEach((tag) => {
    metaData.meta[tag.name] = tag;
  });
}

useMeta(metaData);

async function onNavigate(page) {
  try {
    renderStore.postsPage = page;
    await renderStore.getPosts(window.location.hostname);
    window.scrollTo(0, 0);
  } catch (error) {
    handleError(error);
  }
}
</script>
<style lang="scss" scoped>
.empty-block {
  border: 1px solid var(--blog-border-color, $grey-4);
}
.empty-block-description {
  max-width: 200px;
  opacity: 0.5;
}
</style>

<style lang="scss" scoped>
:deep(.q-pagination) {
  height: 70px;

  &.pagination-mobile {
    margin-top: 24px;

    .q-pagination__content {
      padding-left: 0;
      padding-right: 0;
      gap: 10px;

      .q-btn {
        &:first-child,
        &:last-child {
          position: relative;
          font-size: 14px;
        }
      }
    }
  }

  .q-pagination__content {
    width: 100%;
    display: flex;
    position: relative;
    padding-left: 54px;
    padding-right: 54px;
    gap: 10px;
    justify-content: center;

    .q-btn {
      width: 44px;
      height: 44px;
      border-radius: 10px;
      padding: 0 !important;
      border: 1px solid;
      border-color: var(--blog-border-color, $grey-4);

      .q-focus-helper {
        visibility: hidden;
      }

      &__wrapper {
        border-top: 1px solid transparent;
        border-radius: 0;
        &:hover {
          color: var(--blog-primary-color, $primary);
          border-color: var(--blog-primary-color, $primary);
        }
      }

      .q-btn__content {
        color: var(--blog-font-color, $grey-4);
        opacity: 0.5;
      }

      &[aria-current="true"] {
        background: var(--blog-card-color, #fff);
        border-color: var(--blog-card-color, #fff);

        .q-btn__content {
          opacity: 1;
        }
      }
    }
  }

  .q-pagination__middle {
    flex-wrap: nowrap;
    gap: 10px;
  }
}
</style>
